import posthog from 'posthog-js';

export type AnalyticsConsent = 'granted' | 'denied' | 'unknown';

const CONSENT_STORAGE_KEY = 'schmitt_analytics_consent';
let isInitialized = false;


const canUseStorage = () => typeof window !== 'undefined' && 'localStorage' in window;

export const readConsent = (): AnalyticsConsent => {
  if (!canUseStorage()) return 'unknown';
  const value = window.localStorage.getItem(CONSENT_STORAGE_KEY);
  if (value === 'granted' || value === 'denied') return value;
  return 'unknown';
};

export const writeConsent = (consent: AnalyticsConsent) => {
  if (!canUseStorage()) return;
  if (consent === 'unknown') {
    window.localStorage.removeItem(CONSENT_STORAGE_KEY);
    return;
  }
  window.localStorage.setItem(CONSENT_STORAGE_KEY, consent);
};

const ensureInitialized = () => {
  if (isInitialized) return;
  posthog.init(import.meta.env.VITE_PUBLIC_POSTHOG_KEY, {
    api_host: import.meta.env.VITE_PUBLIC_POSTHOG_HOST,
    person_profiles: 'identified_only',
  });
  isInitialized = true;
};

export const enableAnalytics = () => {
  ensureInitialized();
  posthog.opt_in_capturing();
  posthog.capture('$pageview');

  const customWindow = window as unknown as {
    gtag?: (...args: unknown[]) => void;
  };
  if (customWindow.gtag) {
    customWindow.gtag('consent', 'update', {
      'ad_storage': 'granted',
      'ad_user_data': 'granted',
      'ad_personalization': 'granted',
      'analytics_storage': 'granted'
    });
  }
};

export const disableAnalytics = () => {
  if (isInitialized) {
    posthog.opt_out_capturing();
    posthog.reset();
  }

  const customWindow = window as unknown as {
    gtag?: (...args: unknown[]) => void;
  };
  if (customWindow.gtag) {
    customWindow.gtag('consent', 'update', {
      'ad_storage': 'denied',
      'ad_user_data': 'denied',
      'ad_personalization': 'denied',
      'analytics_storage': 'denied'
    });
  }
};

export const applyConsent = (consent: AnalyticsConsent) => {
  writeConsent(consent);
  if (consent === 'granted') {
    enableAnalytics();
    return;
  }
  if (consent === 'denied') {
    disableAnalytics();
  }
};

export const initIfConsented = () => {
  if (readConsent() === 'granted') {
    enableAnalytics();
  }
};

export const captureEvent = (eventName: string, properties?: Record<string, unknown>) => {
  if (readConsent() !== 'granted') return;
  ensureInitialized();
  posthog.capture(eventName, properties);
};

export const identifyUser = (distinctId: string, properties?: Record<string, unknown>) => {
  if (readConsent() !== 'granted') return;
  ensureInitialized();
  posthog.identify(distinctId, properties);
};

export const trackGoogleAdsConversion = () => {
  const customWindow = window as unknown as {
    gtag?: (...args: unknown[]) => void;
  };
  if (customWindow.gtag) {
    customWindow.gtag('event', 'conversion', {
      'send_to': 'AW-18283596823/Aa0MCLugr8wcEJeYpo5E',
      'value': 1.0,
      'currency': 'EUR'
    });
  }
};

